/**
 * Aura Patch Applier
 *
 * Applies JSON patches to HTML using DOM manipulation.
 * Runs in the browser, works with the iframe preview.
 *
 * Supports two formats:
 * 1. Object: { selector, action, html?, class?, attribute?, value? }
 * 2. Array (NDJSON): [selector, action, value?, extra?]
 */

// ============================================================================
// TYPES
// ============================================================================

export interface Patch {
  selector: string
  action: "setContent" | "setAttribute" | "addClass" | "removeClass" | "remove" | "insertBefore" | "insertAfter"
  html?: string
  attribute?: string
  value?: string
  class?: string
}

/**
 * Array format: [selector, action, value?, extra?]
 * - [0] selector: CSS selector
 * - [1] action: setContent|addClass|removeClass|setAttribute|remove|insertBefore|insertAfter
 * - [2] value: html content, class names, or attribute value
 * - [3] extra: attribute name (only for setAttribute)
 */
export type PatchArray = [string, string, string?, string?]

/**
 * Supported schema version.
 * Must match PATCH_SCHEMA_VERSION in backend.
 */
export const SUPPORTED_PATCH_VERSION = 1

// ============================================================================
// MAIN FUNCTION
// ============================================================================

/**
 * Validate a single patch object.
 */
function isValidPatch(p: any): p is Patch {
  return (
    typeof p === "object" &&
    typeof p.selector === "string" &&
    typeof p.action === "string" &&
    ["setContent", "setAttribute", "addClass", "removeClass", "remove", "insertBefore", "insertAfter"].includes(
      p.action,
    )
  )
}

/**
 * Convert array patch to object patch.
 */
export function arrayToPatch(arr: PatchArray): Patch | null {
  const [selector, action, value, extra] = arr

  if (!selector || !action) return null

  const patch: Patch = {
    selector,
    action: action as Patch["action"],
  }

  switch (action) {
    case "setContent":
    case "insertBefore":
    case "insertAfter":
      patch.html = value
      break
    case "addClass":
    case "removeClass":
      patch.class = value
      break
    case "setAttribute":
      patch.value = value
      patch.attribute = extra
      break
    case "remove":
      // No additional fields needed
      break
    default:
      return null
  }

  return patch
}

/**
 * Apply an array-format patch directly to HTML.
 */
export function applyArrayPatch(
  html: string,
  patchArray: PatchArray,
): { html: string; success: boolean; error?: string } {
  const patch = arrayToPatch(patchArray)
  if (!patch) {
    return { html, success: false, error: "Invalid patch array format" }
  }
  return applySinglePatch(html, patch)
}

/**
 * Apply a single patch to HTML and return the updated HTML.
 */
export function applySinglePatch(html: string, patch: Patch): { html: string; success: boolean; error?: string } {
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, "text/html")
    const elements = doc.querySelectorAll(patch.selector)

    if (elements.length === 0) {
      return { html, success: false, error: `No elements found for: ${patch.selector}` }
    }

    elements.forEach((element) => {
      switch (patch.action) {
        case "setContent":
          if (patch.html !== undefined) {
            element.innerHTML = patch.html
          }
          break
        case "setAttribute":
          if (patch.attribute && patch.value !== undefined) {
            element.setAttribute(patch.attribute, patch.value)
          }
          break
        case "addClass":
          if (patch.class) {
            element.classList.add(...patch.class.split(" ").filter(Boolean))
          }
          break
        case "removeClass":
          if (patch.class) {
            element.classList.remove(...patch.class.split(" ").filter(Boolean))
          }
          break
        case "remove":
          element.remove()
          break
        case "insertBefore":
          if (patch.html) {
            element.insertAdjacentHTML("beforebegin", patch.html)
          }
          break
        case "insertAfter":
          if (patch.html) {
            element.insertAdjacentHTML("afterend", patch.html)
          }
          break
      }
    })

    // Serialize back
    const resultHtml = doc.doctype ? "<!DOCTYPE html>\n" + doc.documentElement.outerHTML : doc.documentElement.outerHTML

    return { html: resultHtml, success: true }
  } catch (error: any) {
    return { html, success: false, error: error.message }
  }
}
